import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import heroImage from '../assets/craftOnline.jpg';
import pipersImage from '../assets/pipers.jpg';

/* ═══════════════ config & helpers ═══════════════ */
const isDev = process.env.NODE_ENV === 'development';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (isDev ? 'http://127.0.0.1:8000/api' : '');
const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || (isDev ? 'https://staging.kaakazini.com' : '');

// Route of the public profile page. Landing + Services use "/craftsmen/:id".
// If your router actually uses "/craftsman/:slug", change this ONE line.
const PROFILE_PATH = '/craftsmen';

// Add real URLs to show the social icons. Empty ones are hidden (no dead "#" links).
const SOCIALS = [
  { icon: 'fab fa-facebook-f',  label: 'Facebook',  url: '' },
  { icon: 'fab fa-x-twitter',   label: 'X',         url: '' },
  { icon: 'fab fa-instagram',   label: 'Instagram', url: '' },
  { icon: 'fab fa-linkedin-in', label: 'LinkedIn',  url: '' },
];


const CATEGORIES = [
  { label: 'All',        icon: 'fas fa-th',              keys: [] },
  { label: 'Plumbing',   icon: 'fas fa-tint',            keys: ['plumb'] },
  { label: 'Electrical', icon: 'fas fa-bolt',            keys: ['electric'] },
  { label: 'Carpentry',  icon: 'fas fa-tools',           keys: ['carpen', 'furniture', 'wood'] },
  { label: 'Painting',   icon: 'fas fa-paint-brush',     keys: ['paint'] },
  { label: 'Tiling',     icon: 'fas fa-border-all',      keys: ['tiling', 'tile'] },
  { label: 'Masonry',    icon: 'fas fa-grip-horizontal', keys: ['mason', 'stone'] },
  { label: 'Metalwork',  icon: 'fas fa-hammer',          keys: ['metal', 'weld', 'steel'] },
  { label: 'Textile',    icon: 'fas fa-cut',             keys: ['textile', 'tailor', 'cloth', 'fashion'] },
];

const PLACEHOLDER = 'https://placehold.co/400x260/fff000/000000?text=KaaKazini';
const norm = (s) => (s || '').toLowerCase();
const matchesCategory = (provider, cat) =>
  cat.keys.length === 0 || cat.keys.some((k) => norm(provider.primary_service).includes(k));

const avatarFallback = (name, size = 88) =>
  `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'User')}&background=fff000&color=000000&size=${size}`;

const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${MEDIA_URL}${path}`;
};

const getCoverImage = (s) =>
  getImageUrl(
    s.gallery_images?.[0]?.image_url ||
      s.services?.[0]?.image_url ||
      s.services?.[0]?.image ||
      s.service_image ||
      null
  );

const getSubLabel = (sv) => (typeof sv === 'string' ? sv : sv?.name || sv?.title || '');

const isTopRated = (s) => {
  const rating = Number(s?.average_rating) || 0;
  const count = s?.review_count ?? s?.reviews_count ?? s?.total_reviews;
  return rating >= 4.5 && (count === undefined || Number(count) >= 3);
};

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const countLabel = (n) => `${n} ${n === 1 ? 'craftsman' : 'craftsmen'}`;

/* ═══════════════ small components ═══════════════ */
function SafeImg({ src, fallback = PLACEHOLDER, alt = '', ...rest }) {
  const [bad, setBad] = useState(false);
  useEffect(() => { setBad(false); }, [src]);
  return <img src={bad || !src ? fallback : src} alt={alt} onError={() => setBad(true)} {...rest} />;
}

function StarRating({ rating }) {
  const n = Number(rating) || 0;
  const full = Math.floor(n);
  const half = n % 1 >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <span className="kk-stars" role="img" aria-label={`${n} out of 5 stars`}>
      {[...Array(full)].map((_, i) => <i key={`f${i}`} className="fas fa-star" aria-hidden="true" />)}
      {half && <i className="fas fa-star-half-alt" aria-hidden="true" />}
      {[...Array(empty)].map((_, i) => <i key={`e${i}`} className="far fa-star" aria-hidden="true" />)}
    </span>
  );
}

function SkeletonCard() {
  return (
    <div className="col">
      <div className="kk-skel-card">
        <div className="kk-skel" style={{ height: 200 }} />
        <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <div className="kk-skel" style={{ width: 44, height: 44, borderRadius: '50%', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div className="kk-skel" style={{ height: 11, width: '65%', borderRadius: 6 }} />
              <div className="kk-skel" style={{ height: 9, width: '40%', borderRadius: 6, marginTop: 6 }} />
            </div>
          </div>
          <div className="kk-skel" style={{ height: 9, borderRadius: 6 }} />
          <div className="kk-skel" style={{ height: 36, borderRadius: 10, marginTop: 4 }} />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ page ═══════════════ */
export default function ServicesPage() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [active, setActive] = useState('All');
  const [search, setSearch] = useState('');
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 700, once: true, offset: 40, disable: () => prefersReducedMotion() });
  }, []);

  useEffect(() => {
    const fn = () => setShowTop(window.scrollY > 500);
    window.addEventListener('scroll', fn, { passive: true });
    return () => window.removeEventListener('scroll', fn);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(false);
    axios
      .get(`${API_BASE_URL}/public-craftsman/`, { signal: ctrl.signal })
      .then((r) =>
        setServices(
          (r.data || [])
            .filter((s) => (s.is_approved || s.status === 'approved') && (s.primary_service || s.profession))
            .map((s) => ({ ...s, primary_service: s.primary_service || s.profession }))
        )
      )
      .catch((err) => {
        if (axios.isCancel(err)) return;
        console.error(err);
        setError(true);
      })
      .finally(() => { if (!ctrl.signal.aborted) setLoading(false); });
    return () => ctrl.abort();
  }, [reloadKey]);

  useEffect(() => { if (!loading) AOS.refresh(); }, [loading]);

  const activeCat = CATEGORIES.find((c) => c.label === active) || CATEGORIES[0];

  // Filtering is derived state — no effect needed
  const filtered = useMemo(() => {
    const q = norm(search).trim();
    return services.filter((s) => {
      if (!matchesCategory(s, activeCat)) return false;
      if (!q) return true;
      return [s.name, s.full_name, s.primary_service, s.location, s.description, (s.services || []).map(getSubLabel).join(' ')]
        .some((v) => norm(v).includes(q));
    });
  }, [services, activeCat, search]);

  // Only offer categories that actually have providers
  const visibleCategories = useMemo(
    () => (loading || error ? CATEGORIES : CATEGORIES.filter((c) => c.label === 'All' || services.some((s) => matchesCategory(s, c)))),
    [services, loading, error]
  );

  // Real average of existing ratings — hidden if there are none
  const avgRating = useMemo(() => {
    const rated = services.map((s) => Number(s.average_rating) || 0).filter((r) => r > 0);
    return rated.length ? rated.reduce((a, b) => a + b, 0) / rated.length : null;
  }, [services]);

  const resetFilters = () => { setActive('All'); setSearch(''); };
  const visibleSocials = SOCIALS.filter((s) => s.url);

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800;900&display=swap');

        @keyframes shimmer   { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes floatUp   { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:translateY(0)} }
        @keyframes fadeRight { from{opacity:0;transform:translateX(24px)} to{opacity:1;transform:translateX(0)} }

        :root {
          --brand:     #000000;
          --brand-d:   #1a1a1a;
          --brand-l:   #f2f2f2;
          --gold:      #fff000;
          --gold-d:    #e0d800;
          --gold-l:    #fffde0;
          --gold-text: #8a6d00;
          --bg:        #ffffff;
          --white:     #ffffff;
          --text:      #000000;
          --muted:     #4d4d4d;
          --border:    #e4e4e4;
        }
        body { background:var(--bg); font-family:'Raleway',sans-serif; color:var(--text); overflow-x:hidden; }
        img { max-width:100%; }
        a:focus-visible, button:focus-visible, input:focus-visible { outline:3px solid var(--gold-d); outline-offset:2px; }
        .kk-stars { color:var(--gold-text); display:inline-flex; gap:2px; font-size:.82rem; }
        .kk-skel { background:linear-gradient(90deg,#f0f0f0 25%,#e4e4e4 50%,#f0f0f0 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
        .kk-skel-card { background:#fff; border-radius:14px; overflow:hidden; border:1.5px solid var(--border); opacity:.7; }

        /* ── BUTTONS ── */
        .kk-btn-gold { display:inline-flex; align-items:center; gap:7px; background:var(--gold); color:#000; border:none; border-radius:12px; padding:13px 26px; font-weight:700; font-size:.93rem; text-decoration:none; cursor:pointer; transition:all .2s; box-shadow:0 4px 18px rgba(255,240,0,.35); font-family:inherit; }
        .kk-btn-gold:hover { background:var(--gold-d); color:#000; transform:translateY(-2px); box-shadow:0 8px 28px rgba(255,240,0,.45); }
        .kk-btn-hire-sm { display:block; text-align:center; background:var(--gold); color:#000; border:none; border-radius:10px; padding:11px 0; font-weight:700; font-size:.86rem; text-decoration:none; cursor:pointer; transition:all .18s; margin-top:auto; font-family:inherit; box-shadow:0 3px 12px rgba(255,240,0,.28); }
        .kk-btn-hire-sm:hover { background:var(--gold-d); color:#000; transform:translateY(-1px); box-shadow:0 6px 20px rgba(255,240,0,.4); }
        .kk-btn-muted { background:#e4e4e4!important; color:#6b6b6b!important; cursor:default!important; transform:none!important; box-shadow:none!important; }
        .kk-btn-reset { background:var(--gold); color:#000; border:none; border-radius:10px; padding:11px 26px; font-weight:700; font-size:.88rem; cursor:pointer; transition:all .18s; font-family:inherit; box-shadow:0 3px 12px rgba(255,240,0,.28); margin-top:16px; }
        .kk-btn-reset:hover { background:var(--gold-d); transform:translateY(-1px); }

        /* ── HERO ── */
        .sv-hero { position:relative; background:var(--brand-d); overflow:hidden; min-height:520px; }
        .sv-hero-bg { position:absolute; inset:0; background-size:cover; background-position:center; opacity:.4; }
        .sv-hero::before { content:''; position:absolute; inset:0; z-index:1; background:linear-gradient(90deg,rgba(0,0,0,.82) 0%,rgba(0,0,0,.55) 60%,rgba(0,0,0,.35) 100%); }
        /* .sv-hero-inner is also Bootstrap's .container — set padding per side, never "A 0 B" */
        .sv-hero-inner { position:relative; z-index:2; display:flex; align-items:center; min-height:520px; padding-top:80px; padding-bottom:60px; padding-left:20px; padding-right:20px; }
        .sv-hero-inner > .row { margin-left:0; margin-right:0; }
        .sv-hero-text { animation:floatUp .85s ease both; }
        .sv-hero-title { font-size:clamp(2rem,4vw,3rem); font-weight:800; color:#fff; line-height:1.15; margin:0 0 16px; }
        .sv-hero-title em { font-style:normal; color:var(--gold); }
        .sv-hero-sub { font-size:.97rem; color:rgba(255,255,255,.85); line-height:1.85; margin:0 0 28px; max-width:480px; }
        .sv-hero-actions { display:flex; flex-wrap:nowrap; gap:12px; }
        .sv-hero-actions .kk-btn-gold { flex:1 1 0; justify-content:center; white-space:nowrap; min-width:0; overflow:hidden; }
        .sv-hero-img-wrap { position:relative; animation:fadeRight .95s ease both; animation-delay:.15s; }
        .sv-hero-img-main { width:100%; height:380px; object-fit:cover; border-radius:16px; box-shadow:0 20px 60px rgba(0,0,0,.35); display:block; }
        .sv-hero-img-badge { position:absolute; bottom:20px; left:16px; background:#fff; border-radius:12px; padding:12px 16px; box-shadow:0 8px 28px rgba(0,0,0,.18); display:flex; align-items:center; gap:10px; min-width:170px; max-width:calc(100% - 32px); }
        .sv-hero-img-badge-icon { width:38px; height:38px; border-radius:50%; background:var(--gold-l); color:var(--gold-text); display:flex; align-items:center; justify-content:center; font-size:.9rem; flex-shrink:0; }
        .sv-hero-img-badge-val { font-size:1.15rem; font-weight:800; color:var(--text); line-height:1; margin:0; }
        .sv-hero-img-badge-lbl { font-size:.68rem; color:var(--muted); font-weight:600; margin:2px 0 0; display:block; }

        /* ── FILTER BAR ── */
        .sv-filter { background:var(--white); padding:14px 0; position:sticky; top:0; z-index:200; box-shadow:0 2px 14px rgba(0,0,0,.07); border-bottom:2px solid var(--gold); }
        .sv-cats { display:flex; gap:7px; flex-wrap:wrap; flex:1; }
        .sv-cat-btn { display:inline-flex; align-items:center; gap:6px; border:1.5px solid var(--border); background:var(--white); color:var(--muted); border-radius:50px; padding:7px 15px; font-size:.78rem; font-weight:600; cursor:pointer; transition:all .18s; white-space:nowrap; font-family:inherit; }
        .sv-cat-btn i { font-size:.7rem; }
        .sv-cat-btn:hover { border-color:var(--brand); color:var(--brand); background:var(--brand-l); }
        .sv-cat-btn.active { background:var(--gold); border-color:var(--gold); color:#000; box-shadow:0 4px 12px rgba(255,240,0,.35); }
        .sv-search-wrap { position:relative; min-width:230px; }
        .sv-search { border:1.5px solid var(--border); border-radius:50px; padding:8px 16px; font-size:.84rem; width:100%; outline:none; transition:border-color .18s,box-shadow .18s; background:var(--white); font-family:inherit; }
        .sv-search:focus { border-color:var(--gold-d); box-shadow:0 0 0 3px rgba(255,240,0,.25); }

        /* ── LISTINGS ── */
        .sv-section { background:var(--bg); padding:52px 0 72px; }
        .sv-results-bar { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; margin-bottom:28px; }
        .sv-results-tag { background:var(--brand-l); color:var(--text); border:1px solid var(--border); border-radius:20px; padding:5px 16px; font-size:.83rem; font-weight:600; }
        .sv-clear-btn { background:var(--white); border:1.5px solid #e0e0e0; color:var(--muted); border-radius:20px; padding:5px 15px; font-size:.81rem; font-weight:600; cursor:pointer; display:inline-flex; align-items:center; gap:6px; transition:all .15s; font-family:inherit; }
        .sv-clear-btn:hover { background:#fee2e2; color:#dc2626; border-color:#fca5a5; }

        /* ── CARDS ── */
        .kk-card { background:#fff; border-radius:14px; overflow:hidden; box-shadow:0 2px 16px rgba(0,0,0,.07); transition:transform .25s,box-shadow .25s,border-color .2s; display:flex; flex-direction:column; height:100%; border:1.5px solid var(--border); position:relative; }
        .kk-card::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(to right,var(--brand),var(--gold)); transform:scaleX(0); transform-origin:left; transition:transform .3s; z-index:1; }
        .kk-card:hover { transform:translateY(-5px); box-shadow:0 12px 36px rgba(0,0,0,.12); border-color:var(--gold); }
        .kk-card:hover::before { transform:scaleX(1); }
        .kk-card-cover { position:relative; height:200px; overflow:hidden; flex-shrink:0; }
        .kk-card-img { width:100%; height:100%; object-fit:cover; transition:transform .4s; }
        .kk-card:hover .kk-card-img { transform:scale(1.06); }
        .kk-badge-trade { position:absolute; top:10px; left:10px; background:rgba(0,0,0,.82); color:#fff; border-radius:20px; padding:3px 11px; font-size:.7rem; font-weight:700; backdrop-filter:blur(4px); max-width:60%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
        .kk-badge-top { position:absolute; top:10px; right:10px; background:rgba(255,240,0,.95); color:#000; border-radius:20px; padding:3px 10px; font-size:.67rem; font-weight:800; }
        .kk-card-body { padding:16px; display:flex; flex-direction:column; gap:9px; flex:1; }
        .kk-card-profile { display:flex; align-items:center; gap:10px; }
        .kk-avatar { width:44px; height:44px; border-radius:50%; object-fit:cover; border:2.5px solid var(--gold); flex-shrink:0; }
        .kk-card-name { font-size:.93rem; font-weight:700; color:var(--text); margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .kk-card-loc { font-size:.74rem; color:var(--muted); display:block; margin-top:1px; }
        .kk-card-rating { display:flex; align-items:center; gap:6px; }
        .kk-card-rating span { font-size:.75rem; color:var(--muted); font-weight:600; }
        .kk-card-desc { font-size:.78rem; color:var(--muted); line-height:1.55; margin:0; flex:1; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }

        /* ── EMPTY / ERROR ── */
        .sv-empty { text-align:center; padding:64px 24px; background:var(--white); border-radius:16px; border:1.5px solid var(--border); }
        .sv-empty h5 { font-size:1.2rem; color:var(--text); font-weight:700; margin-bottom:8px; }
        .sv-empty p { color:var(--muted); font-size:.91rem; margin:0; }

        /* ── FOOTER ── */
        .sv-footer { background:#000; color:#bbb; padding:52px 0 26px; }
        .sv-footer h5 { color:#fff; font-weight:700; font-size:.8rem; letter-spacing:.08em; text-transform:uppercase; margin-bottom:14px; }
        .sv-footer a { color:#bbb; text-decoration:none; font-size:.85rem; transition:color .15s; }
        .sv-footer a:hover { color:var(--gold); }
        .sv-footer p { font-size:.85rem; margin-bottom:6px; }
        .sv-footer-hr { border-color:rgba(255,255,255,.08); margin:26px 0 18px; }
        .sv-social a { font-size:1rem; color:#bbb; margin-right:14px; display:inline-block; transition:color .15s,transform .15s; }
        .sv-social a:hover { color:var(--gold); transform:translateY(-2px); }
        .sv-map-wrap { border-radius:12px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,.3); }
        .kk-back-top { position:fixed; bottom:24px; right:24px; width:44px; height:44px; border-radius:50%; background:var(--gold); color:#000; border:none; font-size:.9rem; display:flex; align-items:center; justify-content:center; box-shadow:0 4px 18px rgba(255,240,0,.45); cursor:pointer; transition:all .2s; z-index:9999; opacity:0; pointer-events:none; }
        .kk-back-top.on { opacity:1; pointer-events:auto; }
        .kk-back-top:hover { background:var(--gold-d); transform:translateY(-2px); }

        /* ── RESPONSIVE ── */
        @media(max-width:992px){
          .sv-hero-inner { min-height:auto; padding-top:64px; padding-bottom:40px; }
          .sv-hero { min-height:auto; }
          .sv-hero-inner > .row > [class*="col-"] { padding-left:0; padding-right:0; }
          .sv-hero-inner > .row { --bs-gutter-y:0; }
          .sv-hero-img-wrap { margin-top:36px; }
          .sv-hero-img-main { height:300px; }
        }
        @media(max-width:768px){
          .sv-hero-inner { padding-top:56px; padding-bottom:36px; }
          .sv-hero-title { font-size:1.9rem; }
          .sv-hero-sub { font-size:.9rem; max-width:100%; }
          .sv-filter { padding:12px 0; }
          .sv-filter .d-flex { flex-direction:column; align-items:stretch; gap:10px!important; }
          .sv-search-wrap { width:100%; min-width:0; order:-1; }
          .sv-search { padding:12px 16px; font-size:.9rem; border-radius:12px; height:46px; }
          .sv-cats { flex-wrap:nowrap; overflow-x:auto; -webkit-overflow-scrolling:touch; scrollbar-width:none; padding-bottom:2px; }
          .sv-cats::-webkit-scrollbar { display:none; }
          .sv-cat-btn { flex-shrink:0; }
          .sv-hero-img-wrap { display:none; }
          .sv-hero-actions { gap:8px; }
          .sv-hero-actions .kk-btn-gold { padding:12px 10px; font-size:.8rem; gap:5px; }
        }
        @media(max-width:576px){
          .sv-hero-inner { padding-left:16px; padding-right:16px; }
          .sv-cats { gap:5px; }
          .sv-hero-title { font-size:1.65rem; }
          .sv-hero-sub { font-size:.86rem; }
        }
        @media(max-width:400px){
          .sv-hero-actions .kk-btn-gold { font-size:.72rem; padding:11px 6px; gap:4px; }
        }
        @media(max-width:360px){
          .sv-hero-actions .kk-btn-gold i { display:none; }
        }
        @media (prefers-reduced-motion: reduce){
          *, *::before, *::after { animation-duration:.01ms!important; animation-iteration-count:1!important; transition-duration:.01ms!important; scroll-behavior:auto!important; }
        }
      `}</style>

      {/* ━━━━━━━━━━ HERO ━━━━━━━━━━ */}
      <section className="sv-hero" id="top">
        <div className="sv-hero-bg" style={{ backgroundImage: `url(${heroImage})` }} aria-hidden="true" />
        <div className="container sv-hero-inner">
          <div className="row align-items-center w-100 g-5">
            <div className="col-lg-6 sv-hero-text">
              <h1 className="sv-hero-title">
                Browse Every Service,<br />Find the Right Craftsman,<br /><em>Right Where You Live.</em>
              </h1>
              <p className="sv-hero-sub">
                Explore skilled plumbers, electricians, carpenters, tailors and more across Kenya. Every craftsman is manually verified before going live.
              </p>
              <div className="sv-hero-actions">
                <Link to="/craftsmen" className="kk-btn-gold"><i className="fas fa-search" aria-hidden="true" />Browse Craftsmen</Link>
                <Link to="/signup" className="kk-btn-gold"><i className="fas fa-user-plus" aria-hidden="true" />Join as Craftsman</Link>
              </div>
            </div>

            <div className="col-lg-6 sv-hero-img-wrap">
              <img src={pipersImage} alt="Skilled craftsmen at work" className="sv-hero-img-main" width="640" height="380" />
              {avgRating && (
                <div className="sv-hero-img-badge">
                  <div className="sv-hero-img-badge-icon"><i className="fas fa-star" aria-hidden="true" /></div>
                  <div>
                    <p className="sv-hero-img-badge-val">{avgRating.toFixed(1)} / 5</p>
                    <span className="sv-hero-img-badge-lbl">Average craftsman rating</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ━━━━━━━━━━ FILTER BAR ━━━━━━━━━━ */}
      <div className="sv-filter" role="navigation" aria-label="Filter by trade">
        <div className="container">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="sv-cats" role="group" aria-label="Trade filters">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.label}
                  className={`sv-cat-btn ${active === cat.label ? 'active' : ''}`}
                  onClick={() => setActive(cat.label)}
                  aria-pressed={active === cat.label}
                >
                  <i className={cat.icon} aria-hidden="true" />
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="sv-search-wrap" role="search">
              <input
                className="sv-search"
                type="search"
                placeholder="Search name, trade, location…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search craftsmen"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ━━━━━━━━━━ LISTINGS ━━━━━━━━━━ */}
      <section className="sv-section" aria-label="Craftsmen listings" aria-live="polite">
        <div className="container">
          {!loading && !error && (
            <div className="sv-results-bar">
              <span className="sv-results-tag">
                <strong>{countLabel(filtered.length)}</strong> available
                {active !== 'All' && <> &middot; <strong>{active}</strong></>}
                {search && <> &middot; &ldquo;{search}&rdquo;</>}
              </span>
              {(active !== 'All' || search) && (
                <button className="sv-clear-btn" onClick={resetFilters}>
                  <i className="fas fa-times" aria-hidden="true" /> Clear filters
                </button>
              )}
            </div>
          )}

          {loading ? (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4">
              {[...Array(8)].map((_, i) => <SkeletonCard key={i} />)}
            </div>
          ) : error ? (
            <div className="sv-empty" role="alert">
              <h5>We couldn&rsquo;t load craftsmen</h5>
              <p>Check your connection and try again.</p>
              <button className="kk-btn-reset" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="sv-empty">
              <h5>No craftsmen found</h5>
              <p>Try a different category or clear your search.</p>
              <button className="kk-btn-reset" onClick={resetFilters}>Show all craftsmen</button>
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4">
              {filtered.map((s, i) => {
                const avatar = getImageUrl(s.profile_url || s.profile || s.profile_image || s.avatar);
                const rating = Number(s.average_rating) || 0;
                const id = s.slug || s.id || '';
                const name = s.full_name || s.name || 'Craftsman';
                return (
                  <div className="col" key={s.id || i} data-aos="fade-up" data-aos-delay={(i % 4) * 50}>
                    <div className="kk-card">
                      <div className="kk-card-cover">
                        <SafeImg src={getCoverImage(s)} alt={s.primary_service || 'Craftsman'} className="kk-card-img" loading="lazy" />
                        <span className="kk-badge-trade">{s.primary_service}</span>
                        {isTopRated(s) && <span className="kk-badge-top">Top Rated</span>}
                      </div>
                      <div className="kk-card-body">
                        <div className="kk-card-profile">
                          <SafeImg src={avatar || avatarFallback(name)} fallback={avatarFallback(name)} alt={name} className="kk-avatar" loading="lazy" />
                          <div style={{ minWidth: 0 }}>
                            <p className="kk-card-name">{name}</p>
                            {s.location && (
                              <span className="kk-card-loc"><i className="fas fa-map-marker-alt me-1" aria-hidden="true" />{s.location}</span>
                            )}
                          </div>
                        </div>
                        {rating > 0 && (
                          <div className="kk-card-rating"><StarRating rating={rating} /><span>{rating.toFixed(1)}</span></div>
                        )}
                        {s.description && <p className="kk-card-desc">{s.description}</p>}
                        {id ? (
                          <Link to={`${PROFILE_PATH}/${id}`} className="kk-btn-hire-sm">
                            View Profile <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
                          </Link>
                        ) : (
                          <span className="kk-btn-hire-sm kk-btn-muted">Unavailable</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ━━━━━━━━━━ FOOTER ━━━━━━━━━━ */}
      <footer className="sv-footer" role="contentinfo">
        <div className="container">
          <div className="row g-5">
            <div className="col-lg-3 col-md-6">
              <h5>Quick Links</h5>
              <ul className="list-unstyled" style={{ lineHeight: '2.2' }}>
                <li><Link to="/">Home</Link></li>
                <li><Link to="/craftsmen">Browse Craftsmen</Link></li>
                <li><Link to="/signup">Become a Craftsman</Link></li>
                <li><Link to="/HireLogin">Hire a Craftsman</Link></li>
              </ul>
            </div>
            <div className="col-lg-4 col-md-6">
              <h5>Contact Us</h5>
              <p><i className="fas fa-map-marker-alt me-2" style={{ color: 'var(--gold)' }} aria-hidden="true" />Kisumu, Kenya</p>
              <p><i className="fas fa-envelope me-2" style={{ color: 'var(--gold)' }} aria-hidden="true" />support@kaakazini.com</p>
              {visibleSocials.length > 0 && (
                <div className="sv-social mt-3">
                  <h5>Follow Us</h5>
                  {visibleSocials.map(({ icon, label, url }) => (
                    <a key={label} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}><i className={icon} aria-hidden="true" /></a>
                  ))}
                </div>
              )}
            </div>
            <div className="col-lg-5">
              <h5>Find Us</h5>
              <div className="sv-map-wrap">
                <iframe
                  title="KaaKazini — Kisumu"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d63828.69947405925!2d34.7106301!3d-0.1022054!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x182aa5b2e0a70b83%3A0x36005f520589fdfc!2sKisumu!5e0!3m2!1sen!2ske!4v1718888888888!5m2!1sen!2ske"
                  width="100%" height="200" style={{ border: 0, display: 'block' }}
                  allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
          <hr className="sv-footer-hr" />
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: '.8rem' }}>
            <p style={{ margin: 0 }}>© {new Date().getFullYear()} <strong style={{ color: '#fff' }}>KaaKazini</strong>. All rights reserved.</p>
            <a href="#top" style={{ color: 'var(--gold)', fontWeight: 600 }}>Back to top <i className="fas fa-arrow-up ms-1" aria-hidden="true" /></a>
          </div>
        </div>
      </footer>

      <button
        className={`kk-back-top ${showTop ? 'on' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'auto' : 'smooth' })}
        aria-label="Back to top"
        tabIndex={showTop ? 0 : -1}
      >
        <i className="fas fa-arrow-up" aria-hidden="true" />
      </button>
    </>
  );
}